/**
 * Ein sehr kleiner Ausschnitt HTML — genug für Fett, Kursiv, Unterstrichen und
 * Aufzählungen, und sonst nichts.
 *
 * Warum eine eigene Reinigung und keine Bibliothek: Was hier erlaubt ist, sind
 * ein Dutzend Auszeichnungen ohne ein einziges Attribut. Eine Bibliothek für
 * diesen Umfang wäre mehr Abhängigkeit als Nutzen — und die Liste unten ist die
 * ganze Regel, sie lässt sich an einem Stück lesen.
 *
 * Gereinigt wird beim **Anzeigen**, nicht nur beim Speichern. Was in der
 * Datenbank steht, ist damit egal: Selbst ein von Hand eingetragenes
 * `<script>` käme nie in die Seite. Umgekehrt wäre eine Reinigung nur beim
 * Schreiben eine Regel, die für alles Bestehende nie gegolten hat.
 *
 * Und der zweite Teil: **alter Text wird gelesen, wie er gemeint war.** Der
 * ganze Bestand ist vor der Auszeichnungsleiste entstanden und behilft sich mit
 * den Zeichen, die man aus Markdown kennt — gemessen am 22.08.2026 stehen in
 * 80 Beschreibungen 249 `**Hervorhebungen**`, 86 Aufzählungszeilen, 70
 * nummerierte, 51 Code-Stellen in Backticks und 25 Trennlinien. Die als
 * Rohtext anzuzeigen hieße, sie als Sternchenhaufen anzuzeigen.
 */

// Was durchkommt. Ohne Attribute — es gibt hier nichts, das eine Adresse, eine
// Farbe oder ein Ereignis tragen dürfte, und damit auch keinen Weg hinein.
const ERLAUBTE_TAGS = new Set([
	'B',
	'STRONG',
	'I',
	'EM',
	'U',
	'UL',
	'OL',
	'LI',
	'BR',
	'P',
	'DIV',
	'CODE',
	'HR',
	'H3',
	'H4'
])

// Tags, deren Inhalt gar nicht erst übernommen wird. Bei allen anderen
// unbekannten Tags bleibt der Text stehen und nur die Auszeichnung fällt weg —
// bei diesen wäre der „Text" der Programmcode selbst.
const GIFTIGE_TAGS = new Set(['SCRIPT', 'STYLE', 'IFRAME', 'OBJECT', 'EMBED', 'TEMPLATE'])

/**
 * Trägt der Text überhaupt Auszeichnungen?
 *
 * Der Bestand ist reiner Text. Der darf nicht als HTML gelesen werden — ein
 * `<` in einer Fehlerbeschreibung („Platzhalter <Firmenname>") wäre sonst der
 * Anfang eines Tags und der Rest der Zeile verschwände. Deshalb die
 * Unterscheidung: Nur was wie unsere eigene Ausgabe aussieht, wird als
 * Auszeichnung gelesen; alles andere geht durch die Umschrift unten.
 */
export const istAusgezeichnet = wert =>
	typeof wert === 'string' &&
	/<(b|strong|i|em|u|ul|ol|li|br|p|div|code|hr|h3|h4)(\s|>|\/)/i.test(wert)

const escape = text =>
	String(text ?? '')
		.replace(/&/g, '&amp;')
		.replace(/</g, '&lt;')
		.replace(/>/g, '&gt;')

/*
 * ------------------------------------------------------------------------
 * Die Umschrift des alten Behelfs
 * ------------------------------------------------------------------------
 *
 * Bewusst **nicht** vollständiges Markdown. Was hier steht, ist an dem
 * gemessen, was im Bestand wirklich vorkommt — und vor allem an dem, was dabei
 * schiefgehen kann. Zwei Fälle haben die Regeln geformt:
 *
 *   - **Der Gender-Stern.** „Schüler*innen" steht überall. Läse man jeden
 *     Stern als Auszeichnung, würde aus zwei davon in einer Zeile ein kursiver
 *     Bereich quer durch den Satz. Ein Stern zählt deshalb nur, wenn vor ihm
 *     ein Zeilenanfang oder ein Leerzeichen steht — mitten im Wort nie.
 *   - **Der Unterstrich.** Im Bestand kommt er genau einmal paarweise vor, und
 *     zwar in einem Dateinamen („…_cerificate.pdf" → "…"). Unterstriche werden
 *     deshalb **gar nicht** als Auszeichnung gelesen.
 *
 * Die Umschrift greift nur bei Text **ohne** Auszeichnung. Wer die Leiste
 * benutzt hat, bekommt sein HTML unverändert; die Sternchen sind dann Zeichen
 * wie andere auch.
 */

// Code-Stellen kommen zuerst dran und werden für die übrigen Regeln
// weggesperrt: In `**nicht fett**` innerhalb von Backticks sollen die Sterne
// stehen bleiben. Das Nullzeichen kann in escaptem Text nicht vorkommen.
const MARKE = '\u0000'

const codeWegsperren = (text, ablage) =>
	text.replace(/`([^`\n]+)`/g, (_, inhalt) => {
		ablage.push(inhalt)
		return `${MARKE}${ablage.length - 1}${MARKE}`
	})

const MARKEN_SUCHE = new RegExp(`${MARKE}(\\d+)${MARKE}`, 'g')

const codeZurueck = (text, ablage) =>
	text.replace(MARKEN_SUCHE, (_, nummer) => `<code>${ablage[Number(nummer)]}</code>`)

/**
 * Auszeichnungen innerhalb einer Zeile.
 *
 * Der Inhalt darf kein `*` enthalten und weder mit einem Leerzeichen beginnen
 * noch enden — damit greift die Regel auf `** **` oder `a ** b ** c` nicht zu.
 */
const inZeile = text => {
	const codes = []
	let ergebnis = codeWegsperren(text, codes)

	// **fett** — der häufigste Fall im Bestand.
	ergebnis = ergebnis.replace(/\*\*([^\s*][^*\n]*?[^\s*]|[^\s*])\*\*/g, '<b>$1</b>')

	/*
	 * *kursiv* — mit Wortgrenzen auf beiden Seiten.
	 *
	 * Vor dem öffnenden Stern muss ein Zeilenanfang, ein Leerzeichen oder eine
	 * öffnende Klammer stehen (das schützt „Schüler*innen"), und der Inhalt darf
	 * nicht mit `.` oder `/` beginnen (das schützt Dateimuster wie `*.md`).
	 * Die Randzeichen werden mitgefangen und wieder eingesetzt.
	 */
	ergebnis = ergebnis.replace(
		/(^|[\s(["„])\*([^\s*./][^*\n]*?[^\s*]|[^\s*./])\*($|[\s)\].,;:!?"“])/gm,
		'$1<i>$2</i>$3'
	)

	return codeZurueck(ergebnis, codes)
}

const AUFZAEHLUNG = /^(\s*)[-*+]\s+(.*)$/
const NUMMERIERT = /^(\s*)\d+[.)]\s+(.*)$/
// Ab zwei Leerzeichen Einzug gilt ein Punkt als Unterpunkt. Tiefer als eine
// Stufe geht der Bestand nicht (gemessen: 5 eingerueckte von 159 Zeilen), und
// mehr Stufen in einem Fenster dieser Groesse zu zeigen brauchte niemand.
const EINZUG = 2
const UEBERSCHRIFT = /^(#{1,6})\s+(.*)$/
// Nur eine Zeile aus lauter Strichen. Mit dem Leerzeichen dahinter wäre es ein
// Aufzählungspunkt — deshalb hier kein `\s`.
const TRENNLINIE = /^-{3,}$/

/**
 * Zeilen zu Blöcken: Absätze, Listen, Überschriften, Trennlinien.
 *
 * Innerhalb eines Absatzes bleibt jeder Zeilenumbruch ein Umbruch (`<br>`) —
 * so, wie es die frühere Darstellung mit `white-space: pre-wrap` tat. Zwischen
 * Absätzen entsteht dagegen ein echter Abstand statt zweier Leerzeilen.
 */
const inBloecke = escaped => {
	const teile = []
	let absatz = []
	let liste = null

	const absatzSchliessen = () => {
		// Leerzeilen an den Rändern gehören zur Trennung, nicht in den Absatz.
		while (absatz.length && !absatz[absatz.length - 1].trim()) absatz.pop()
		if (absatz.length) teile.push(`<p>${absatz.map(inZeile).join('<br>')}</p>`)
		absatz = []
	}

	/*
	 * Eine Liste ausgeben — mit einer Stufe Unterpunkte.
	 *
	 * Eingerueckte Punkte gehoeren in den `<li>` darueber, nicht daneben:
	 * Nebeneinander verloere „Art der Eingabe:" seine drei Unterpunkte und alles
	 * staende gleichrangig da.
	 */
	const listeSchliessen = () => {
		if (!liste) return

		let html = ''
		let i = 0
		while (i < liste.punkte.length) {
			const punkt = liste.punkte[i]

			// Ein Unterpunkt ohne Punkt darueber ist keiner — dann eben normal.
			if (punkt.tief === 0) {
				const kinder = []
				let j = i + 1
				while (j < liste.punkte.length && liste.punkte[j].tief > 0) {
					kinder.push(liste.punkte[j])
					j += 1
				}

				const unterliste = kinder.length
					? `<${kinder[0].tag}>${kinder
							.map(kind => `<li>${inZeile(kind.inhalt)}</li>`)
							.join('')}</${kinder[0].tag}>`
					: ''
				html += `<li>${inZeile(punkt.inhalt)}${unterliste}</li>`
				i = j
			} else {
				html += `<li>${inZeile(punkt.inhalt)}</li>`
				i += 1
			}
		}

		teile.push(`<${liste.tag}>${html}</${liste.tag}>`)
		liste = null
	}

	const alleSchliessen = () => {
		absatzSchliessen()
		listeSchliessen()
	}

	const zumPunkt = (tag, einzug, inhalt) => {
		absatzSchliessen()
		const tief = einzug.length >= EINZUG ? 1 : 0

		// Die Art (Striche oder Zahlen) entscheidet nur auf der obersten Stufe
		// ueber eine neue Liste — ein Unterpunkt darf ruhig anders aussehen als
		// sein Elternpunkt, so steht es im Bestand.
		if (liste && tief === 0 && liste.tag !== tag) listeSchliessen()
		if (!liste) liste = { tag, punkte: [] }
		liste.punkte.push({ tief, tag, inhalt })
	}

	for (const zeile of escaped.split(/\r?\n/)) {
		const roh = zeile.trim()

		if (TRENNLINIE.test(roh)) {
			alleSchliessen()
			teile.push('<hr>')
			continue
		}

		const ueberschrift = UEBERSCHRIFT.exec(roh)
		if (ueberschrift) {
			alleSchliessen()
			// Zwei Stufen genügen: Ein Fenster dieser Größe braucht keine sechs.
			const tag = ueberschrift[1].length <= 2 ? 'h3' : 'h4'
			teile.push(`<${tag}>${inZeile(ueberschrift[2])}</${tag}>`)
			continue
		}

		const punkt = AUFZAEHLUNG.exec(zeile)
		if (punkt) {
			zumPunkt('ul', punkt[1], punkt[2])
			continue
		}

		const nummer = NUMMERIERT.exec(zeile)
		if (nummer) {
			zumPunkt('ol', nummer[1], nummer[2])
			continue
		}

		/*
		 * Jede Zeile, die kein Listenpunkt ist, beendet die Liste. Eine leere
		 * Zeile schliesst zusaetzlich den Absatz — sonst schleppte der Abstand
		 * zwischen zwei Absaetzen einen leeren Umbruch mit.
		 */
		listeSchliessen()
		if (!roh) absatzSchliessen()
		else absatz.push(zeile)
	}

	alleSchliessen()
	return teile.join('')
}

/**
 * Räumt einen HTML-Ausschnitt auf alles zusammen, was oben erlaubt ist.
 *
 * Unbekannte Tags werden **aufgelöst**, nicht gelöscht: Ihr Text bleibt. Wer
 * aus einem anderen Programm etwas hereinkopiert, soll seine Sätze behalten und
 * nur die fremde Formatierung verlieren.
 */
export const bereinige = html => {
	if (typeof document === 'undefined') return ''

	const behaelter = document.createElement('div')
	behaelter.innerHTML = String(html ?? '')

	const gehe = knoten => {
		// Rückwärts: Das Auflösen ersetzt einen Knoten durch mehrere, eine
		// vorwärts laufende Schleife liefe dabei über ihre eigenen Nachträge.
		for (const kind of Array.from(knoten.childNodes).reverse()) {
			if (kind.nodeType === Node.TEXT_NODE) continue

			if (kind.nodeType !== Node.ELEMENT_NODE) {
				// Kommentare und alles Übrige haben in einem Beitrag nichts zu suchen.
				kind.remove()
				continue
			}

			if (GIFTIGE_TAGS.has(kind.tagName)) {
				kind.remove()
				continue
			}

			gehe(kind)

			if (ERLAUBTE_TAGS.has(kind.tagName)) {
				// Erlaubt heißt: das Tag, nicht seine Beigaben. `style`, `class`
				// und `on…` fallen hier weg.
				for (const attr of Array.from(kind.attributes)) kind.removeAttribute(attr.name)
			} else {
				kind.replaceWith(...Array.from(kind.childNodes))
			}
		}
	}

	gehe(behaelter)
	return behaelter.innerHTML
}

/**
 * Was in die Seite geschrieben wird — für ausgezeichneten wie für alten Text.
 *
 * Alter Text wird maskiert und dann umgeschrieben: aus `**so**` wird fett, aus
 * einer Zeile mit `- ` ein Aufzählungspunkt. Maskiert **vor** der Umschrift,
 * damit `<Firmenname>` sein Zeichen behält und trotzdem kein Tag daraus wird.
 */
export const alsHtml = wert => {
	if (!wert) return ''
	if (!istAusgezeichnet(wert)) return inBloecke(escape(wert))
	return bereinige(wert)
}

/**
 * Der reine Text dahinter — für Vorschauen, Zeichenzählung und alles, was
 * Auszeichnungen nicht anzeigen kann.
 *
 * Auch der alte Behelf fällt weg: Wer 250 Zeichen abzählt, soll die Sternchen
 * nicht mitzählen, die niemand zu sehen bekommt.
 */
export const alsText = wert => {
	if (!wert) return ''

	const behaelter = document.createElement('div')
	behaelter.innerHTML = alsHtml(wert)
	// Ein Listenpunkt und ein Absatz sind ein Zeilenumbruch, kein Wortzwischenraum.
	behaelter
		.querySelectorAll('li, p, div, br, hr, h3, h4')
		.forEach(el => el.before(document.createTextNode('\n')))
	return (behaelter.textContent || '').replace(/\n{3,}/g, '\n\n').trim()
}

/**
 * Ist überhaupt etwas geschrieben worden?
 *
 * `<p><br></p>` ist das, was ein leer geräumtes Eingabefeld hinterlässt — für
 * den Browser Inhalt, für die Leserin nichts. Ohne diese Prüfung wäre der
 * Senden-Knopf aktiv und es ginge ein leerer Beitrag hinaus.
 */
export const hatInhalt = wert => alsText(wert).trim().length > 0
